import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  MapPin, 
  Phone, 
  Ruler, 
  Clock, 
  ChevronRight, 
  CheckCircle2, 
  Building, 
  ShieldCheck, 
  ChevronDown, 
  Sparkles,
  ArrowRight,
  X,
  ChevronLeft
} from 'lucide-react';
import { Button } from './Button';
import { PROJECT_META } from '../data/projects';
import { useTheme } from '../context/ThemeContext';
import { SEO } from './SEO';
import { LeadCaptureForm } from './home/LeadCaptureForm';
import { TrustStrip } from './home/TrustStrip';

interface LocationPageProps {
  city: 'Hyderabad' | 'Vijayawada' | 'Bangalore';
}

const CITY_DETAILS = {
  Hyderabad: {
    title: "Best Interior Designers in Hyderabad | Aura Spaces",
    description: "Award-winning home & commercial interior designers in Hyderabad. Premium design solutions for 2BHK, 3BHK flats, luxury villas & modern offices in Narsingi, Gachibowli, Tellapur, Kokapet & Madhapur. Starting from ₹3.5L.",
    keywords: "interior designers in Hyderabad, best home interiors Hyderabad, modular kitchen Hyderabad, Narsingi interior design, Gachibowli interior designers, Kokapet luxury interiors, Aura Spaces Hyderabad",
    geoRegion: "IN-TG",
    geoPlacename: "Hyderabad",
    heroTitle: "Premium Interior Designers in Hyderabad",
    heroSubtitle: "Transforming residential flats, luxury villas, and office spaces across Hyderabad with award-winning custom woodwork and modular setups.",
    tagline: "Flagship Design Studio & Automated Manufacturing Hub",
    studioName: "Aura Spaces Hyderabad (Narsingi)",
    address: "Puppalaguda Main Rd, near YSR Statue, Narsingi, Hyderabad, Telangana 500089",
    phone: "9989697942",
    mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3808.178!2d78.3573!3d17.3846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDIzJzA0LjYiTiA3OMKwMjEnMjYuMyJF!5e0!3m2!1sen!2sin!4v1680000000000",
    localitiesServed: [
      "Narsingi", "Gachibowli", "Tellapur", "Kokapet", "Madhapur", 
      "Kondapur", "Manikonda", "Nanakramguda", "Jubilee Hills", "Banjara Hills"
    ],
    faqs: [
      {
        question: "What is the cost of home interior design in Hyderabad?",
        answer: "A complete premium 2BHK interior package starts from ₹3.5L. Standard 3BHK home interiors range between ₹5.5L and ₹8.5L, while premium 3BHKs or luxury villas incorporating custom acrylic finishes, lacquered glass, and smart wardrobes range from ₹10L to ₹18L+."
      },
      {
        question: "Where is your design studio located in Hyderabad?",
        answer: "Our flagship design studio and material experience center are located on Puppalaguda Main Road near the YSR Statue in Narsingi, Hyderabad. Clients are welcome to visit to touch, feel, and choose from over 1000+ laminate, veneer, and modular hardware samples."
      },
      {
        question: "Do you offer a warranty on wood materials and kitchen fittings?",
        answer: "Absolutely. We offer a comprehensive 5-year warranty on all modular woodwork (including modular kitchens and custom wardrobes) and lifetime service support. We utilize strictly BWR (Boiling Water Resistant) plywood with Hettich or Blum hardware."
      },
      {
        question: "What is the delivery timeline for projects in Hyderabad?",
        answer: "We offer a strict 45-day delivery guarantee. Once the 3D designs are locked and signed off, we pre-manufacture the modules in our state-of-the-art automated factory and complete the on-site assembly within 7-10 days."
      }
    ],
    schema: {
      "@context": "https://schema.org",
      "@type": "InteriorDesignBusiness",
      "name": "Aura Spaces Hyderabad",
      "alternateName": "Best Interior Designers in Hyderabad | Aura Spaces",
      "url": "https://www.auraspaces.net/interior-designers-in-hyderabad",
      "logo": "https://www.auraspaces.net/logo.png",
      "image": "https://www.auraspaces.net/images/hero/hero-1.jpg",
      "description": "Award-winning interior designers in Hyderabad for luxury homes, modular kitchens, wardrobes, and commercial office turnkey interiors.",
      "telephone": "+919989697942",
      "email": "operations@auraspaces.net",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Puppalaguda Main Rd, near YSR Statue, Narsingi",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "postalCode": "500089",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 17.3846,
        "longitude": 78.3573
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "10:00",
        "closes": "19:00"
      },
      "priceRange": "₹₹₹",
      "areaServed": ["Hyderabad", "Narsingi", "Gachibowli", "Tellapur", "Kokapet", "Kondapur", "Madhapur", "Manikonda", "Nanakramguda"]
    },
    ongoingProjects: [
      { src: "/images/ongoing/hyderabad/hyd-1.jpg", locality: "Kokapet", stage: "Electrical & Ceiling Wiring", details: "Routing ceiling electrical wiring and conduits for custom spotlight fixtures." },
      { src: "/images/ongoing/hyderabad/hyd-2.jpg", locality: "Kokapet", stage: "Civil Design Layout", details: "Taping CAD blueprints to raw brick wall for structural markings and wall grooving." },
      { src: "/images/ongoing/hyderabad/hyd-3.jpg", locality: "Kokapet", stage: "Modular Wardrobe Frame", details: "Assembling bedroom wardrobe carcass frame compartments using BWR plywood." },
      { src: "/images/ongoing/hyderabad/hyd-4.jpg", locality: "Kokapet", stage: "Kitchen Wall Cabinets", details: "Fitting modular overhead cabinets with clean wood laminates above the counter space." },
      { src: "/images/ongoing/hyderabad/hyd-5.jpg", locality: "Kokapet", stage: "Living Room TV Wall", details: "Installing structural plywood backboards for a full-height TV media panel." },
      { src: "/images/ongoing/hyderabad/hyd-6.jpg", locality: "Kokapet", stage: "Kitchen Base Cabinets", details: "Aligning soft-close drawer units and preparing modular kitchen carcass sections." }
    ]
  },
  Vijayawada: {
    title: "Top Interior Designers in Vijayawada | Aura Spaces",
    description: "Expert interior designers in Vijayawada for premium homes, villas, and modern workspaces. Modular kitchens, sliding wardrobes, and turnkey interior design at Nidamanuru, Benz Circle, and surrounding areas.",
    keywords: "interior designers in Vijayawada, best home interiors Vijayawada, modular kitchen Vijayawada, Nidamanuru interior design, Benz Circle interior design, Aura Spaces Vijayawada",
    geoRegion: "IN-AP",
    geoPlacename: "Vijayawada",
    heroTitle: "Premium Interior Designers in Vijayawada",
    heroSubtitle: "Delivering world-class residential woodwork, high-gloss modular kitchens, and custom wardrobes optimized for Vijayawada's modern living spaces.",
    tagline: "Experience Center & Design Studio in Nidamanuru",
    studioName: "Aura Spaces Vijayawada (Nidamanuru)",
    address: "Vijayalakshmi Enclave, Nidamanuru, Vijayawada, Andhra Pradesh 521104",
    phone: "9989697942",
    mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3825.123!2d80.6480!3d16.5062!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTbCsDMwJzIyLjMiTiA4MMKwMzgenNTIuOCJF!5e0!3m2!1sen!2sin!4v1680000000000",
    localitiesServed: [
      "Nidamanuru", "Benz Circle", "Kanuru", "Poranki", "Gollapudi", 
      "Labbipet", "Gunadala", "Guntur", "Amaravati", "Moghalrajpuram"
    ],
    faqs: [
      {
        question: "Do you have a physical design showroom in Vijayawada?",
        answer: "Yes, our Vijayawada experience center is located in Vijayalakshmi Enclave, Nidamanuru. You can visit us to examine modular wardrobes, modular kitchen setups, partition panels, and layout blueprints."
      },
      {
        question: "How much does a modular kitchen cost in Vijayawada?",
        answer: "Our high-durability modular kitchens in Vijayawada start from ₹1.5L. They are crafted using 18mm BWR (Boiling Water Resistant) marine plywood and finished with anti-scratch laminates or acrylic shutters to withstand coastal humidity."
      },
      {
        question: "Do you take up projects outside Vijayawada, like Guntur or Amaravati?",
        answer: "Yes, we actively execute projects in Guntur, Mangalagiri, and the Amaravati capital region. Our Vijayawada-based design teams handle delivery and installation seamlessly across these neighboring cities."
      },
      {
        question: "What is the warranty policy on wood works?",
        answer: "We offer a solid 5-year replacement warranty on all woodwork and modular fittings. We use premium certified hardware brands like Ebco, Hettich, or Hafele to ensure hassle-free operation for decades."
      }
    ],
    schema: {
      "@context": "https://schema.org",
      "@type": "InteriorDesignBusiness",
      "name": "Aura Spaces Vijayawada",
      "alternateName": "Top Interior Designers in Vijayawada | Aura Spaces",
      "url": "https://www.auraspaces.net/interior-designers-in-vijayawada",
      "logo": "https://www.auraspaces.net/logo.png",
      "image": "https://www.auraspaces.net/images/hero/hero-1.jpg",
      "description": "Premium home interiors, modular kitchens, and wardrobes in Vijayawada. Visit our design studio at Nidamanuru.",
      "telephone": "+919989697942",
      "email": "operations@auraspaces.net",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Vijayalakshmi Enclave, Nidamanuru",
        "addressLocality": "Vijayawada",
        "addressRegion": "Andhra Pradesh",
        "postalCode": "521104",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 16.5062,
        "longitude": 80.6480
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "10:00",
        "closes": "19:00"
      },
      "priceRange": "₹₹₹",
      "areaServed": ["Vijayawada", "Nidamanuru", "Benz Circle", "Kanuru", "Poranki", "Gollapudi", "Guntur", "Amaravati"]
    },
    ongoingProjects: [
      { src: "/images/ongoing/vijayawada/vij-1.jpg", locality: "Benz Circle", stage: "Open Crockery Cabinet", details: "Installing tall open shelving cabinet with marble-pattern backing sheets in the dining area." },
      { src: "/images/ongoing/vijayawada/vij-2.jpg", locality: "Benz Circle", stage: "TV Entertainment Console", details: "Assembling living room television console panels and low cabinets." },
      { src: "/images/ongoing/vijayawada/vij-3.jpg", locality: "Benz Circle", stage: "Dining Side Credenza", details: "Fitting base cabinet carcasses made of SAINIK 710 BWR plywood with open niches." },
      { src: "/images/ongoing/vijayawada/vij-4.jpg", locality: "Benz Circle", stage: "Modular Pantry & Cabinets", details: "Aligning L-shape modular kitchen wall cabinets and a tall appliance pantry tower." },
      { src: "/images/ongoing/vijayawada/vij-5.jpg", locality: "Benz Circle", stage: "Wardrobe & Study Desk", details: "Constructing bedroom wardrobe carcass shelves and an integrated window study counter." }
    ]
  },
  Bangalore: {
    title: "Luxury Interior Designers in Bangalore | Aura Spaces",
    description: "Premium interior design services in Bangalore. Award-winning designers for luxury apartments, premium villas, modular kitchens, and modern office spaces in Whitefield, Sarjapur, HSR Layout & Koramangala.",
    keywords: "interior designers in Bangalore, premium interiors Bangalore, luxury apartments Bangalore, Whitefield interior designers, HSR Layout interiors, Aura Spaces Bangalore",
    geoRegion: "IN-KA",
    geoPlacename: "Bangalore",
    heroTitle: "Luxury Interior Designers in Bangalore",
    heroSubtitle: "Crafting bespoke residential spaces and state-of-the-art office setups for discerning clients across Bengaluru's top tech corridors.",
    tagline: "Dedicated Design and Consultation Teams",
    studioName: "Aura Spaces Bangalore Regional Office",
    address: "Whitefield, Bengaluru, Karnataka 560066",
    phone: "9989697942",
    mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d124430.40488667527!2d77.6780!3d12.9698!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae11d4d0000001%3A0xf69a1c1d81de2cfd!2sWhitefield%2C%20Bengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1680000000000",
    localitiesServed: [
      "Whitefield", "Sarjapur Road", "HSR Layout", "Koramangala", "Indiranagar", 
      "Electronic City", "Bellandur", "Marathahalli", "Outer Ring Road", "Jayanagar"
    ],
    faqs: [
      {
        question: "Which areas in Bangalore do you serve?",
        answer: "We offer end-to-end design and execution across all key areas in Bangalore, focusing heavily on Whitefield, Sarjapur Road, Bellandur, HSR Layout, Koramangala, Indiranagar, and Electronic City."
      },
      {
        question: "What is the typical pricing for a 3BHK apartment in Bangalore?",
        answer: " Bounded by high-quality cabinetry and custom fittings, 3BHK interior projects in Bangalore typically start from ₹5.5L. Multi-wardrobe packages with premium finishes and false ceilings average around ₹8L to ₹12L depending on complexity."
      },
      {
        question: "Do you handle commercial office interior designs in Bangalore?",
        answer: "Yes, we specialize in turnkey commercial spaces, co-working environments, and agile IT workspaces (e.g. projects like Empire Estates and Novel Office). We manage layout planning, acoustic partitioning, HVAC integration, and lighting."
      },
      {
        question: "Do you provide customized layouts and 3D visualization?",
        answer: "Yes. Every client receives detailed 2D blueprints and high-fidelity 3D visualization renders of their rooms, kitchens, and wardrobes so they can preview their home before manufacturing starts."
      }
    ],
    schema: {
      "@context": "https://schema.org",
      "@type": "InteriorDesignBusiness",
      "name": "Aura Spaces Bangalore",
      "alternateName": "Luxury Interior Designers in Bangalore | Aura Spaces",
      "url": "https://www.auraspaces.net/interior-designers-in-bangalore",
      "logo": "https://www.auraspaces.net/logo.png",
      "image": "https://www.auraspaces.net/images/hero/hero-1.jpg",
      "description": "Premium interior decorators and designers in Bangalore for apartments, luxury villas, and corporate offices.",
      "telephone": "+919989697942",
      "email": "operations@auraspaces.net",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Whitefield",
        "addressLocality": "Bangalore",
        "addressRegion": "Karnataka",
        "postalCode": "560066",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 12.9698,
        "longitude": 77.6780
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "10:00",
        "closes": "19:00"
      },
      "priceRange": "₹₹₹",
      "areaServed": ["Bangalore", "Bengaluru", "Whitefield", "Sarjapur", "HSR Layout", "Koramangala", "Indiranagar", "Electronic City", "Bellandur"]
    },
    ongoingProjects: []
  }
};

export function LocationPage({ city }: LocationPageProps) {
  const details = CITY_DETAILS[city];
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [ongoingLightboxIndex, setOngoingLightboxIndex] = useState<number | null>(null);

  // Scroll to top on city change or mount
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [city]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (ongoingLightboxIndex === null || !details.ongoingProjects) return;
      if (e.key === 'ArrowRight') {
        setOngoingLightboxIndex((prev) => (prev !== null ? (prev + 1) % details.ongoingProjects.length : null));
      }
      if (e.key === 'ArrowLeft') {
        setOngoingLightboxIndex((prev) => (prev !== null ? (prev - 1 + details.ongoingProjects.length) % details.ongoingProjects.length : null));
      }
      if (e.key === 'Escape') {
        setOngoingLightboxIndex(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ongoingLightboxIndex, details]);

  // Filter projects by location
  const localProjects = React.useMemo(() => {
    return Object.entries(PROJECT_META)
      .filter(([_, proj]) => {
        const loc = proj.location.toLowerCase();
        if (city === 'Hyderabad') {
          return loc.includes('hyderabad') || loc.includes('gachibowli') || loc.includes('kokapet') || loc.includes('narsingi') || loc.includes('madhapur') || loc.includes('kondapur') || loc.includes('kphb') || loc.includes('nanakramguda') || loc.includes('hafeezpet');
        }
        if (city === 'Vijayawada') {
          return loc.includes('vijayawada');
        }
        if (city === 'Bangalore') {
          return loc.includes('bengaluru') || loc.includes('bangalore') || loc.includes('whitefield') || loc.includes('sarjapur');
        }
        return false;
      })
      .map(([slug, proj]) => ({ slug, ...proj }));
  }, [city]);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Construct dynamic QAPage schema for FAQs
  const faqSchema = React.useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": details.faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  }, [details]);

  // Combine Business and FAQ schemas
  const compositeSchema = React.useMemo(() => {
    const cleanBusinessSchema = { ...details.schema } as any;
    delete cleanBusinessSchema["@context"];
    cleanBusinessSchema["@type"] = ["LocalBusiness", "InteriorDesignBusiness"];

    const cleanFaqSchema = { ...faqSchema } as any;
    delete cleanFaqSchema["@context"];

    return {
      "@context": "https://schema.org",
      "@graph": [
        cleanBusinessSchema,
        cleanFaqSchema
      ]
    };
  }, [details, faqSchema]);

  return (
    <div className={`min-h-screen transition-colors duration-500 relative overflow-hidden ${
      isDark ? 'bg-[#0B0F1A] text-gray-200' : 'bg-slate-50 text-slate-800'
    }`}>
      <SEO 
        title={details.title}
        description={details.description}
        keywords={details.keywords}
        geoRegion={details.geoRegion}
        geoPlacename={details.geoPlacename}
        schema={compositeSchema}
      />

      {/* Decorative Blurs */}
      <div className="absolute top-1/4 -left-48 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/3 -right-48 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[140px] pointer-events-none" />

      {/* Hero Section */}
      <section className={`relative pt-32 pb-16 sm:pt-40 sm:pb-24 flex items-center overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0F172A]' : 'bg-slate-100'
      }`}>
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(201,162,107,0.25),rgba(255,255,255,0))]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-accent/15 border border-accent/25 rounded-md text-[10px] uppercase font-poppins tracking-widest font-extrabold text-accent">
                <Sparkles className="w-3.5 h-3.5" /> Aura Spaces {city}
              </span>
              
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-poppins font-black leading-tight tracking-tight ${
                isDark ? 'text-white' : 'text-slate-950'
              }`}>
                {details.heroTitle}
              </h1>
              
              <p className={`text-base sm:text-lg leading-relaxed max-w-2xl pt-2 ${
                isDark ? 'text-gray-400' : 'text-slate-600'
              }`}>
                {details.heroSubtitle}
              </p>

              <div className="pt-4 flex flex-wrap gap-4">
                <Button 
                  variant="accent" 
                  size="md"
                  onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Book Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="glass" 
                  size="md"
                  onClick={() => document.getElementById('local-projects')?.scrollIntoView({ behavior: 'smooth' })}
                  className={isDark ? "border-white/10 hover:bg-white/10 text-white" : "border-slate-300 hover:bg-slate-200 text-slate-800"}
                >
                  View Local Projects
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* Localized stats / Why Aura Spaces */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className={`text-3xl sm:text-4xl font-poppins font-black tracking-tight mb-4 ${
            isDark ? 'text-white' : 'text-slate-950'
          }`}>
            Why Choose Aura Spaces in {city}?
          </h2>
          <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
            We combine high-end customized design architecture with precision German manufacturing to deliver beautiful spaces.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: "45-Day Handover", desc: "Guaranteed on-time delivery from 3D sign-off to modular installation or we pay your rent.", icon: Clock },
            { title: "5-Year Warranty", desc: "Comprehensive structural warranty on wardrobes, cabinets, and modular kitchen hardware.", icon: ShieldCheck },
            { title: "Bespoke Production", desc: "Calibrated BWR marine ply and premium finishes manufactured in our automated factory.", icon: Building },
            { title: "100% Transparent", desc: "No hidden costs. Detailed material lists, itemized pricing, and stage-by-stage approvals.", icon: Ruler }
          ].map((item, i) => (
            <div 
              key={i}
              className={`rounded-2xl p-6 border transition-all duration-300 hover:scale-[1.02] ${
                isDark ? 'bg-[#111827]/40 border-white/[0.04]' : 'bg-white border-slate-200/50 shadow-sm'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center mb-5">
                <item.icon className="w-6 h-6 text-accent" />
              </div>
              <h3 className={`font-poppins font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Localized Projects Portfolio */}
      <section id="local-projects" className={`py-16 sm:py-24 transition-colors duration-500 ${
        isDark ? 'bg-[#111827]/40' : 'bg-slate-100/50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className={`text-3xl sm:text-4xl font-poppins font-black tracking-tight mb-2 ${
                isDark ? 'text-white' : 'text-slate-950'
              }`}>
                Featured {city} Projects
              </h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Explore our real-world completed handovers in {city}.
              </p>
            </div>
          </div>

          {localProjects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {localProjects.map((project, i) => (
                <div 
                  key={project.slug}
                  className={`rounded-2xl overflow-hidden border shadow-sm group hover:shadow-lg transition-all duration-300 flex flex-col ${
                    isDark ? 'bg-[#1E293B] border-gray-700' : 'bg-white border-gray-100'
                  }`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img 
                      src={project.hero} 
                      alt={project.title} 
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="text-[10px] uppercase font-poppins tracking-wider font-extrabold px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white rounded-md">
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className={`font-poppins font-bold text-xl mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs text-accent font-semibold mb-3">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{project.location}</span>
                      </div>
                      <p className={`text-sm leading-relaxed mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        {project.description}
                      </p>
                    </div>
                    <div className="pt-4 border-t border-gray-100 dark:border-white/[0.04] flex items-center justify-between">
                      <span className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        Area: {project.area}
                      </span>
                      <button 
                        onClick={() => navigate(`/case-study/${project.slug}`)}
                        className="inline-flex items-center gap-1 text-xs font-bold text-accent hover:translate-x-1 transition-transform"
                      >
                        Explore Case Study <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`p-8 text-center rounded-2xl border ${isDark ? 'bg-[#1E293B] border-gray-700' : 'bg-white border-gray-200'}`}>
              <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>No local showcases currently listed. Contact us to speak with a designer.</p>
            </div>
          )}
        </div>
      </section>

      {/* Ongoing Projects Section */}
      {details.ongoingProjects && details.ongoingProjects.length > 0 && (
        <section className={`py-16 sm:py-24 transition-colors duration-500 ${
          isDark ? 'bg-[#0B0F1A]' : 'bg-white'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <span className="text-xs uppercase font-poppins tracking-wider font-extrabold text-accent bg-accent/15 px-3 py-1 rounded-md">
                Active Construction & Woodwork
              </span>
              <h2 className={`text-3xl sm:text-4xl font-poppins font-black tracking-tight mt-3 mb-2 ${
                isDark ? 'text-white' : 'text-slate-950'
              }`}>
                Ongoing Site Execution in {city}
              </h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                Real-time raw updates straight from our active residential and commercial sites in {city}. No edits, just pure material & execution excellence.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {details.ongoingProjects.map((proj: any, idx: number) => (
                <div
                  key={idx}
                  onClick={() => setOngoingLightboxIndex(idx)}
                  className={`group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer border ${
                    isDark ? 'border-white/[0.04] bg-[#0E1626]' : 'border-slate-200/50 bg-slate-100'
                  }`}
                >
                  <img
                    src={proj.src}
                    alt={`${proj.stage} in ${proj.locality}, ${city}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-85 md:opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white">
                    <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold text-accent mb-0.5">
                      {proj.locality}
                    </span>
                    <strong className="text-xs sm:text-sm font-semibold truncate">
                      {proj.stage}
                    </strong>
                    <p className="text-[10px] sm:text-xs text-gray-300 line-clamp-1 mt-1 font-light">
                      {proj.details}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ongoing Projects Lightbox */}
      <AnimatePresence>
        {ongoingLightboxIndex !== null && details.ongoingProjects && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 sm:p-8"
            onClick={() => setOngoingLightboxIndex(null)}
          >
            <button
              onClick={() => setOngoingLightboxIndex(null)}
              className="absolute top-6 right-6 text-white/75 hover:text-white transition-colors cursor-pointer"
              aria-label="Close Lightbox"
            >
              <X size={28} />
            </button>

            {details.ongoingProjects.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOngoingLightboxIndex((ongoingLightboxIndex - 1 + details.ongoingProjects.length) % details.ongoingProjects.length);
                  }}
                  className="absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Previous Image"
                >
                  <ChevronLeft size={24} />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOngoingLightboxIndex((ongoingLightboxIndex + 1) % details.ongoingProjects.length);
                  }}
                  className="absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Next Image"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            <div
              className="max-w-4xl max-h-[80vh] flex flex-col items-center gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={details.ongoingProjects[ongoingLightboxIndex].src}
                alt={details.ongoingProjects[ongoingLightboxIndex].stage}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              />
              <div className="text-center text-white max-w-lg">
                <span className="text-[10px] uppercase font-poppins tracking-widest font-extrabold text-accent">
                  {details.ongoingProjects[ongoingLightboxIndex].locality} • {details.ongoingProjects[ongoingLightboxIndex].stage}
                </span>
                <p className="text-sm mt-1 text-gray-300 font-light">
                  {details.ongoingProjects[ongoingLightboxIndex].details}
                </p>
                <div className="text-xs text-white/40 mt-2">
                  {ongoingLightboxIndex + 1} / {details.ongoingProjects.length}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Localized FAQ Accordion */}
      <section className="py-16 sm:py-24 max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className={`text-3xl sm:text-4xl font-poppins font-black tracking-tight mb-4 ${
            isDark ? 'text-white' : 'text-slate-950'
          }`}>
            FAQs About Interior Design in {city}
          </h2>
          <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
            Find quick answers regarding modular pricing, local showroom operations, and materials.
          </p>
        </div>

        <div className="space-y-4">
          {details.faqs.map((faq, i) => (
            <div 
              key={i}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
                isDark 
                  ? openFaqIndex === i ? 'bg-[#1E293B]/70 border-accent/30' : 'bg-[#111827]/40 border-white/[0.04]'
                  : openFaqIndex === i ? 'bg-white border-accent/30 shadow-md' : 'bg-white border-slate-200/50 shadow-xs'
              }`}
            >
              <button
                onClick={() => toggleFaq(i)}
                className="w-full px-6 py-5 flex items-center justify-between text-left font-poppins font-bold text-base sm:text-lg focus:outline-none"
              >
                <span className={isDark ? 'text-white' : 'text-slate-900'}>{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-accent shrink-0 transition-transform duration-300 ${
                  openFaqIndex === i ? 'rotate-180' : ''
                }`} />
              </button>
              
              <AnimatePresence initial={false}>
                {openFaqIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className={`px-6 pb-6 pt-1 text-sm sm:text-base leading-relaxed ${
                      isDark ? 'text-gray-300' : 'text-slate-650'
                    }`}>
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      {/* Localities Served List Section */}
      <section className={`py-12 border-t border-b transition-colors duration-500 ${
        isDark ? 'bg-white/[0.01] border-white/[0.04]' : 'bg-slate-50 border-slate-200/40'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className={`text-[10px] uppercase font-poppins tracking-widest font-extrabold block mb-4 ${
            isDark ? 'text-gray-450' : 'text-slate-400'
          }`}>
            NEIGHBORHOODS SERVED IN {city.toUpperCase()}
          </span>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {details.localitiesServed.map((loc) => (
              <span 
                key={loc}
                className={`text-xs font-semibold px-4.5 py-2 rounded-full border ${
                  isDark 
                    ? 'bg-[#111827] border-white/[0.04] text-gray-400' 
                    : 'bg-white border-slate-200 text-slate-600'
                }`}
              >
                {loc}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Studio Contact / Live Maps */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          <div className="space-y-6">
            <h2 className={`text-3xl sm:text-4xl font-poppins font-black tracking-tight ${
              isDark ? 'text-white' : 'text-slate-950'
            }`}>
              Visit Our {city} Studio
            </h2>
            <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
              Meet our architects, inspect wood core overlays, try modular kitchen cabinet hinges, and choose high-end finishes.
            </p>

            <div className="space-y-4 pt-2">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0 mt-1">
                  <MapPin className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h4 className={`font-poppins font-bold text-sm uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-slate-800'}`}>Address</h4>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{details.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0 mt-1">
                  <Phone className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h4 className={`font-poppins font-bold text-sm uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-slate-800'}`}>Phone</h4>
                  <a href={`tel:${details.phone}`} className="text-sm font-semibold text-accent hover:underline">{details.phone}</a>
                </div>
              </div>
            </div>
          </div>

          <div className={`rounded-3xl overflow-hidden aspect-[16/10] sm:aspect-[16/9] border shadow-md relative z-10 ${
            isDark ? 'border-white/[0.08]' : 'border-slate-200/50'
          }`}>
            <iframe
              src={details.mapEmbed}
              title={`Aura Spaces ${city} Studio Location`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>

        </div>
      </section>

      {/* Lead Capture Form Section */}
      <section id="consultation" className="relative z-10">
        <LeadCaptureForm />
      </section>
    </div>
  );
}
